import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'

// Fundo da tela de login: o poster é o que pinta primeiro e é o elemento que o
// navegador mede como LCP; o vídeo é enriquecimento e só entra depois que a página já
// está utilizável. O poster nunca é removido, fica por baixo do vídeo, o que evita
// piscada na troca e qualquer deslocamento de layout.
//
// O vídeo só é armado quando as três condições valem: viewport de desktop (no telemóvel
// ele fica 90% escondido atrás do cartão e custa dados e bateria), sem
// `prefers-reduced-motion`, e sem sinal de rede fraca.

// Um único arquivo, H.264. AV1 e VP9 foram medidos e descartados: com este material
// o VP9 não ficou menor que o H.264, e o AV1, apesar de ~40% menor, o Safari só
// decodifica por hardware (Mac Intel/M1/M2 não tocam) e sem hardware o consumo de
// energia dispara — caro demais para um enfeite, num parque de notebooks corporativos.
const VIDEO_SRC = '/login/bg.mp4'

function shouldPlay() {
  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
    if (!window.matchMedia('(min-width: 1024px) and (pointer: fine)').matches) return false
    const c = navigator.connection
    if (c && (c.saveData || /(^|-)2g$|^slow-2g$/.test(c.effectiveType || ''))) return false
    return true
  } catch {
    return false
  }
}

export default function VideoBackdrop({ pauseLabel, playLabel }) {
  const ref = useRef(null)
  const [ready, setReady] = useState(false)
  const [paused, setPaused] = useState(false)
  const [armed, setArmed] = useState(false)
  // Espelhos em ref porque o listener de visibilidade é registrado UMA vez (deps []) e
  // fecharia sobre os valores do primeiro render, quando os dois são false. Sem isto o
  // ramo de retomada nunca roda: o fundo congela ao voltar de outra aba.
  const pausedRef = useRef(false)
  const armedRef = useRef(false)

  useEffect(() => {
    const v = ref.current
    if (!v || !shouldPlay()) return

    let cancelled = false
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 800))
    const handle = idle(() => {
      if (cancelled) return
      v.muted = true // em JSX o atributo muted não é confiável; sem isto o autoplay é recusado
      v.preload = 'auto'
      v.addEventListener('canplay', () => {
        v.play().then(() => { setReady(true); setArmed(true); armedRef.current = true }).catch(() => {})
      }, { once: true })
      v.load()
    }, { timeout: 2500 })

    const onVis = () => {
      if (document.hidden) v.pause()
      else if (!pausedRef.current && armedRef.current) v.play().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelled = true
      ;(window.cancelIdleCallback || clearTimeout)(handle)
      document.removeEventListener('visibilitychange', onVis)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = () => {
    const v = ref.current
    if (!v) return
    if (v.paused) {
      v.play().catch(() => {})
      setPaused(false); pausedRef.current = false
    } else {
      v.pause()
      setPaused(true); pausedRef.current = true
    }
  }

  return (
    <div className="absolute inset-0 overflow-hidden bg-slate-900">
      {/* Duas declarações em ordem: a segunda só vale onde `image-set` com `type()` é
          suportado. Sem a primeira, o Safari anterior à 17 descarta a regra inteira e o
          fundo vira um retângulo chapado (o poster nativo do <video> não salva, porque
          o elemento fica em opacity-0 até tocar). */}
      <div
        className="absolute inset-0 bg-cover bg-center login-poster"
        aria-hidden="true"
      />
      {/* Sem o atributo `autoplay`: com ele o navegador ignora o preload="none" e baixa
          o vídeo já no primeiro paint, que é exatamente o que se quer evitar. Quem manda
          tocar é o efeito acima, depois que a página está utilizável. */}
      <video
        ref={ref}
        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`}
        poster="/login/poster.avif"
        preload="none"
        loop
        playsInline
        aria-hidden="true"
        tabIndex={-1}
        disablePictureInPicture
      >
        <source src={VIDEO_SRC} type='video/mp4; codecs="avc1.640028"' />
      </video>

      {/* Scrim determinístico: o contraste do formulário não pode depender de qual
          frame está na tela. Gradiente direcional (mais denso à esquerda, onde fica o
          cartão) + vinheta + um véu uniforme. */}
      <div
        className="absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'linear-gradient(100deg, rgba(6,14,22,.92) 0%, rgba(6,14,22,.72) 38%, rgba(6,14,22,.30) 100%),' +
            'radial-gradient(120% 90% at 50% 50%, transparent 30%, rgba(0,0,0,.5) 100%)',
        }}
      />

      {/* WCAG 2.2.2: movimento que dura mais de 5 segundos precisa de um controle
          operável. O prefers-reduced-motion sozinho não basta, porque muita gente
          afetada nunca liga a preferência do sistema. */}
      {armed && (
        <button
          type="button"
          onClick={toggle}
          title={paused ? playLabel : pauseLabel}
          aria-label={paused ? playLabel : pauseLabel}
          className="absolute bottom-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full
                     bg-black/40 text-white/80 backdrop-blur transition hover:bg-black/60 hover:text-white
                     focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          {paused ? <Play size={16} /> : <Pause size={16} />}
        </button>
      )}
    </div>
  )
}
